import { AnimationEvent } from '@angular/animations';
import { DialogRef } from '@angular/cdk/dialog';
import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { filter, timer } from 'rxjs';

import { AuthService } from '@features/auth';
import { BasketService } from '@features/checkout/services';
import { CustomPagesService } from '@features/custom-pages';

import { slideInOutAnimation } from '../animations';
import { LogoutButtonComponent } from '../logout-button';

@Component({
  selector: 'app-navigation-drawer',
  imports: [RouterLink, LogoutButtonComponent],
  templateUrl: './navigation-drawer.component.html',
  styleUrl: './navigation-drawer.component.scss',
  animations: [slideInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationDrawerComponent implements OnInit, AfterViewInit {
  #dialogRef = inject(DialogRef);
  #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  protected auth = inject(AuthService);
  protected basket = inject(BasketService);

  #router = inject(Router);
  #pages = inject(CustomPagesService);

  protected navigation = this.#pages.sortedMenuItems;

  protected navRoute = CustomPagesService.navRoute;

  protected animationState = signal<string>('out');
  protected redirectUrl = signal('/');

  ngOnInit() {
    this.redirectUrl.set(this.#router.url);

    this.#router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.closeDialog();
    });
  }

  ngAfterViewInit() {
    this.animationState.set('in');
  }

  /**
   * Once the slide-in settles, clear the lingering `translateX(0)` transform so
   * iOS Safari recomputes the drawer's touch hit-test region. Without this the
   * region stays at the animation's start position (`translateX(100%)`, off the
   * right edge), so taps on the links land on empty space and never navigate.
   */
  protected onAnimationDone(event: AnimationEvent) {
    if (!this.#isBrowser || event.toState !== 'in') {
      return;
    }

    const element = event.element as HTMLElement;
    element.style.transform = 'none';
    // Force a synchronous reflow to flush the stale hit-test region.
    void element.offsetHeight;
  }

  protected closeDialog() {
    this.animationState.set('out');
    timer(200).subscribe(() => {
      this.#dialogRef.close();
      if (this.#isBrowser) {
        window.scrollTo(0, 0);
      }
    });
  }
}
